import {
  useMutation,
  type UseMutationOptions,
  useQueryClient
} from "@tanstack/react-query";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";

import type { ClaimHistorySearchSchema } from "@/routes/disbursement/claim-history";
import type { ClaimOrderSearchSchema } from "@/routes/disbursement/claim-orders";

import type {
  SubmitClaimOrderRequestDto,
  SubmitClaimOrderResponseDto
} from "../../dtos/submit-claim-order-dto";
import { OrderQueryKeys } from "../../generators/order-query-keys";
import { OrderService } from "../../services/order-service";

type UsePostSubmitClaimOrderMutationProps = {
  options?: UseMutationOptions<
    SubmitClaimOrderResponseDto,
    unknown,
    SubmitClaimOrderRequestDto
  >;
};

export const usePostSubmitClaimOrderMutation = ({
  options
}: UsePostSubmitClaimOrderMutationProps = {}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (s) => s.location.pathname
  });
  const rawSearch = useSearch({ strict: false });

  return useMutation({
    mutationFn: async (payload) =>
      await OrderService.submitClaimOrder({ payload }),
    ...options,
    onSuccess: async (...args) => {
      const isClaimHistoryPage = pathname.startsWith(
        "/disbursement/claim-history"
      );

      if (isClaimHistoryPage) {
        const { page, q, status, sort } =
          rawSearch as unknown as ClaimHistorySearchSchema;

        const currentPageKey = OrderQueryKeys.getClaimHistoryList({
          limit: 10,
          page,
          q,
          status,
          sort
        }).queryKey;

        // Invalidate and refetch current page
        await queryClient.invalidateQueries({ queryKey: currentPageKey });

        // If the page is empty and it's not the first page, go back one page
        const newData = (queryClient.getQueryData(currentPageKey) as any)?.data;
        if (Array.isArray(newData) && newData.length === 0 && page > 1) {
          const previousPageKey = OrderQueryKeys.getClaimHistoryList({
            limit: 10,
            page: page - 1,
            q,
            status,
            sort
          }).queryKey;
          await queryClient.invalidateQueries({ queryKey: previousPageKey });

          navigate({
            to: "/disbursement/claim-history",
            search: { q, page: page - 1, status, sort }
          });
        }
      } else {
        const { page, q } = rawSearch as unknown as ClaimOrderSearchSchema;

        const currentPageKey = OrderQueryKeys.getClaimOrderList({
          limit: 10,
          page,
          q
        }).queryKey;

        // Invalidate and refetch current page
        await queryClient.invalidateQueries({ queryKey: currentPageKey });

        // If the page is empty and it's not the first page, go back one page
        const newData = (queryClient.getQueryData(currentPageKey) as any)?.data;
        if (Array.isArray(newData) && newData.length === 0 && page > 1) {
          const previousPageKey = OrderQueryKeys.getClaimOrderList({
            limit: 10,
            page: page - 1,
            q
          }).queryKey;
          await queryClient.invalidateQueries({ queryKey: previousPageKey });

          navigate({
            to: "/disbursement/claim-orders",
            search: { q, page: page - 1 }
          });
        }
      }

      // Invalidate badge counts
      await queryClient.invalidateQueries({
        queryKey: OrderQueryKeys.getClaimOrderBadgeCount().queryKey
      });
      await queryClient.invalidateQueries({
        queryKey: OrderQueryKeys.getClaimHistoryBadgeCount().queryKey
      });

      options?.onSuccess?.(...args);
    }
  });
};
